import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { AppFooter } from "../components/layout/AppShell";
import { GoogleSignInButton } from "../components/GoogleSignInButton";
import { InAppBrowserHandoff } from "../components/InAppBrowserHandoff";
import { SampleReportTeaser } from "../components/SampleReportTeaser";
import { SkedMark } from "../components/SkedMark";
import { Turnstile } from "../components/Turnstile";
import { Alert, AlertDescription, AlertTitle } from "../components/ui/alert";
import { usePageMeta } from "../hooks/usePageMeta";
import { fetchBootstrap } from "../lib/api";
import { captureFirstTouch, readFirstTouch } from "../lib/attribution";
import { withBase } from "../lib/base-path";
import { currentInAppBrowser } from "../lib/in-app-browser";
import { consumePrefillEmail, readPrefillEmail } from "../lib/prefill-email-store";
import { isSafeAppPath } from "../../shared/auth/app-path";
import { appendAttributionParams } from "../../shared/leadgen/attribution";
import { PREFILL_EMAIL_PARAM } from "../../shared/leadgen/prefill-email";

const TURNSTILE_ENFORCED_HOSTS = new Set([
  "staging.trafficsnapshot.skedsocial.com",
  "trafficsnapshot.skedsocial.com",
  // M15 — production now serves from skedsocial.com/tools/traffic-snapshot.
  "skedsocial.com",
]);

function turnstileActive(): boolean {
  if (typeof window === "undefined") return false;
  // Only the two real hosts whitelist the widget sitekey, so only they
  // enforce Turnstile. Localhost is intentionally NOT enforced: the
  // server already bypasses verification when APP_ENV=development (see
  // server/security/turnstile.ts), and the widget can't load on
  // localhost anyway — gating the Connect button on a token that can
  // never arrive just strands local dev. The `?turnstile=test` E2E path
  // re-enables a test widget via the `showTurnstile` clause below.
  const host = window.location.hostname;
  return TURNSTILE_ENFORCED_HOSTS.has(host);
}

// Cloudflare's published always-pass Turnstile sitekey for automated
// testing (pairs with the test secret on the server). Only used when
// `?turnstile=test` is on the URL AND the env isn't production — lets
// Chrome MCP / Playwright drive the flow end-to-end without tripping
// Turnstile's bot-detection layer (error 600010).
const TURNSTILE_TEST_SITE_KEY = "1x00000000000000000000AA";

/** An app path to land on after Google, or /app. Only same-origin /app
 *  paths pass, and never a loop back to this page. */
function safeReturnTo(raw: string | null): string {
  return raw !== null && isSafeAppPath(raw) && !raw.startsWith("/app/connect") ? raw : "/app";
}

export function ConnectPage() {
  const [params] = useSearchParams();
  const location = useLocation();
  const error = params.get("error");
  const returnTo = safeReturnTo(params.get("return_to"));
  const prefillEmail = useMemo(() => readPrefillEmail(), []);
  const inAppBrowser = useMemo(() => currentInAppBrowser(), []);
  // Auto-E2E mode. Populated from the URL once — the widget key is
  // fixed for the lifetime of the page. We still honour the prod-env
  // gate when bootstrap resolves (see below); this flag alone doesn't
  // disable anything on prod, it just signals the client path.
  const turnstileTestMode = params.get("turnstile") === "test";
  const [newsletterOptIn, setNewsletterOptIn] = useState(true);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  // Site key is fetched from /api/bootstrap so staging + prod can point
  // at different Turnstile sites on the Cloudflare dashboard. Today our
  // single Turnstile site whitelists both hostnames, so the fallback in
  // Turnstile.tsx is valid on both — we still defer rendering the
  // widget until `bootstrapLoaded` resolves so a future env-split
  // doesn't cause a brief 110200 hostname-mismatch flash on warm-cache
  // renders where the Turnstile script initialises before the real key
  // lands.
  const [turnstileSiteKey, setTurnstileSiteKey] = useState<string | null>(null);
  const [bootstrapLoaded, setBootstrapLoaded] = useState(false);
  const [envIsProduction, setEnvIsProduction] = useState(false);
  // An anonymous visitor's Settings link bounces back here without the
  // ad's UTMs (MKT-698), so it is only a link once we know they are in.
  const [authenticated, setAuthenticated] = useState(false);
  // Enforced hosts always show the widget; localhost shows it only for
  // the `?turnstile=test` E2E path, so normal local dev is never gated.
  const showTurnstile = turnstileActive() || (turnstileTestMode && !envIsProduction);
  useEffect(() => {
    let cancelled = false;
    void fetchBootstrap()
      .then((b) => {
        if (cancelled) return;
        setTurnstileSiteKey(b.turnstileSiteKey ?? null);
        setEnvIsProduction(b.env === "production");
        setAuthenticated(b.authenticated);
        setBootstrapLoaded(true);
      })
      .catch(() => {
        // Bootstrap failure isn't fatal — let the widget render against
        // the hard-coded fallback key (valid for both hostnames).
        // Safe default: assume production so a bootstrap failure with
        // `?turnstile=test` on a prod URL can't surface the misleading
        // "E2E test mode" label. The server gate still rejects in
        // either case; this keeps the visual truth consistent.
        if (!cancelled) {
          setEnvIsProduction(true);
          setBootstrapLoaded(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);
  // E2E test mode is only honoured off-prod. If bootstrap says we're
  // on production, the `?turnstile=test` URL param is silently ignored
  // — so a crafted URL can't downgrade prod to the test sitekey.
  const e2eMode = turnstileTestMode && !envIsProduction;
  const effectiveSiteKey = e2eMode ? TURNSTILE_TEST_SITE_KEY : turnstileSiteKey;
  // Stable references — otherwise Turnstile's render effect (which has
  // `onToken`/`onError`/`onExpired` in its deps) would re-initialise
  // the widget on every ConnectPage render that followed a token state
  // change.
  const clearTurnstileToken = useCallback(() => setTurnstileToken(null), []);
  // When Turnstile is active on this host, disable Connect until a
  // token lands. Prevents a click during the tiny reset → re-challenge
  // window from firing an auth redirect that the server would bounce
  // back with ?error=missing_token.
  const needsTurnstileToken = showTurnstile && !turnstileToken;
  // People can land straight on /app/connect (ads, shared links), so
  // this page captures first touch too. No-ops when the landing page
  // already stored one this tab session.
  useEffect(() => {
    captureFirstTouch();
  }, []);
  usePageMeta({
    title: "Connect GA4 · Traffic Snapshot",
    description: "Connect your GA4 property to Traffic Snapshot and get a social-first report.",
  });

  function connect() {
    const base = withBase(`/auth/google/start?return_to=${encodeURIComponent(returnTo)}`);
    const newsletterParam = newsletterOptIn ? "" : "&newsletter=false";
    const turnstileParam = turnstileToken
      ? `&cf-turnstile-response=${encodeURIComponent(turnstileToken)}`
      : "";
    // Carry `?turnstile=test` through to the auth route so the server
    // switches to the paired test secret for siteverify. Server also
    // gates on APP_ENV !== "production".
    const e2eParam = e2eMode ? "&turnstile=test" : "";
    const emailParam = prefillEmail
      ? `&${PREFILL_EMAIL_PARAM}=${encodeURIComponent(prefillEmail)}`
      : "";
    // From the no-GA4 screen: ask Google for its account chooser.
    const switchParam = params.get("switch") === "1" ? "&switch=1" : "";
    // Replay the first touch onto the auth URL. The server stashes it in
    // the OAuth state blob so it survives the round trip to Google and
    // lands on the user row in the callback. Over-budget URLs shed
    // referrer, then landing_path — never the UTMs or click IDs.
    consumePrefillEmail();
    window.location.href = appendAttributionParams(
      `${base}${newsletterParam}${turnstileParam}${e2eParam}${emailParam}${switchParam}`,
      readFirstTouch(),
    );
  }

  return (
    <div className="flex min-h-dvh flex-col bg-peach text-ink antialiased">
      {/* SKE-3471 — removed the outer `<header>` wordmark. All the
       *  brand attribution + navigation lives inside the center card
       *  now (SkedMark at top, `by Sked Social` eyebrow, `Back to
       *  Traffic Snapshot` link at the bottom), so an external
       *  wordmark was duplication that drew the eye off the card. */}
      <main className="flex flex-1 items-center justify-center px-4 py-8 sm:px-6 sm:py-12">
        <div className="w-full max-w-lg">
          <div className="rounded-[20px] border border-neutral-950/5 bg-white p-6 shadow-lg sm:p-8 md:p-10">
            <SkedMark aria-hidden="true" className="mb-5 size-12" />
            {/* SKE-3427 — Connect-page eyebrow migrated from legacy
             *  .eyebrow-caps (uppercase Inter) to the new .eyebrow canon
             *  (Quarto italic sentence case). Pink ring stays as the
             *  brand-attribution accent for the Sked Social attribution
             *  on this page. */}
            <span className="mb-[14px] inline-block rounded-full border border-pink-200 px-[14px] py-[7px] font-serif text-[15px] italic text-pink-600">
              by Sked Social
            </span>
            <h1 className="mt-3 font-display text-[34px] font-[900] sm:text-[40px] leading-[1.05] tracking-[-0.02em] text-ink text-balance">
              Connect your{" "}
              <span className="font-serif italic font-semibold tracking-[-0.01em] text-purple-500">
                GA4 property
              </span>
              .
            </h1>
            <p className="mt-5 max-w-[48ch] text-[15px] leading-[1.55] text-neutral-700 text-pretty">
              {inAppBrowser
                ? "Your report is one GA4 connect away."
                : "Your report is one GA4 connect away. GA4 data stays read-only: fetched live, with a short-lived cache for the derived report. No row-level GA4 storage."}
            </p>

            {inAppBrowser ? (
              <div className="mt-6">
                <InAppBrowserHandoff
                  browser={inAppBrowser}
                  prefillEmail={prefillEmail}
                  turnstile={{
                    show: showTurnstile,
                    ready: bootstrapLoaded,
                    siteKey: effectiveSiteKey,
                    testMode: e2eMode,
                  }}
                />
              </div>
            ) : null}

            {/* SKE-3471 — was `sm:grid-cols-3` inside a max-w-lg card,
             *  which gave each column ~130px and forced every label to
             *  wrap to 2 lines. Single-column stack reads much cleaner:
             *  icon + bold label + dimmed explainer on one row each,
             *  divide-y hairlines keep visual rhythm without adding
             *  card chrome. */}
            {inAppBrowser ? null : (
              <ul
                role="list"
                className="mt-7 divide-y divide-purple-200/70 rounded-xl bg-purple-50/60 px-4 py-2 text-[13px] leading-[1.4] text-neutral-700"
              >
                <li className="flex items-start gap-3 py-3">
                  <i
                    className="fa-regular fa-shield-check mt-0.5 shrink-0 text-sm text-purple-600"
                    aria-hidden="true"
                  />
                  <div className="min-w-0">
                    <p className="font-semibold text-ink">Read-only GA4 scope</p>
                    <p className="text-neutral-600">
                      GA4 data is read-only; sign-in uses email + profile.
                    </p>
                  </div>
                </li>
                <li className="flex items-start gap-3 py-3">
                  <i
                    className="fa-regular fa-clock mt-0.5 shrink-0 text-sm text-purple-600"
                    aria-hidden="true"
                  />
                  <div className="min-w-0">
                    <p className="font-semibold text-ink">Live fetch · brief cache</p>
                    <p className="text-neutral-600">
                      No warehouse, no pipelines. The derived report caches briefly for performance.
                    </p>
                  </div>
                </li>
                <li className="flex items-start gap-3 py-3">
                  <i
                    className="fa-regular fa-arrow-right-from-bracket mt-0.5 shrink-0 text-sm text-purple-600"
                    aria-hidden="true"
                  />
                  <div className="min-w-0">
                    <p className="font-semibold text-ink">Revoke any time</p>
                    <p className="text-neutral-600">One click from Settings, any time.</p>
                  </div>
                </li>
              </ul>
            )}

            {error ? (
              <Alert variant="destructive" className="mt-6">
                <AlertTitle>GA4 connection failed</AlertTitle>
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            ) : null}

            {inAppBrowser ? null : (
              <div className="mt-8 space-y-4">
                {/* Centered horizontally: Google's branding spec keeps
                 *  the button at natural width (auto + max-w-400px), so
                 *  we wrap in a flex container rather than stretching the
                 *  button itself. */}
                <div className="flex justify-center">
                  <GoogleSignInButton
                    onClick={connect}
                    label="Continue with Google to connect GA4"
                    disabled={needsTurnstileToken}
                  />
                </div>
                <p className="text-center text-[13px] text-neutral-600 text-pretty">
                  Sign in with the Google account that has your GA4 access. Viewer is enough.
                </p>

                {showTurnstile ? (
                  <div className="pt-1">
                    {bootstrapLoaded ? (
                      <Turnstile
                        onToken={setTurnstileToken}
                        onError={clearTurnstileToken}
                        onExpired={clearTurnstileToken}
                        siteKey={effectiveSiteKey}
                      />
                    ) : (
                      <div
                        aria-busy="true"
                        aria-label="Loading security challenge"
                        className="h-16 animate-pulse rounded-md bg-neutral-100"
                      />
                    )}
                    {e2eMode ? (
                      <p className="mt-2 text-[11px] font-mono uppercase tracking-wide text-amber-600">
                        E2E test mode. The always-pass Turnstile sitekey is in use.
                      </p>
                    ) : null}
                  </div>
                ) : null}

                <label
                  htmlFor="newsletter-opt-in"
                  className="flex cursor-pointer items-start gap-3 rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-3 text-sm text-neutral-800 hover:border-neutral-300"
                >
                  <span className="h-lh flex shrink-0 items-center">
                    <span className="group inline-grid size-4 grid-cols-1">
                      <input
                        id="newsletter-opt-in"
                        name="newsletter-opt-in"
                        type="checkbox"
                        checked={newsletterOptIn}
                        onChange={(e) => setNewsletterOptIn(e.target.checked)}
                        className="checked:border-purple-600 checked:bg-purple-600 focus-visible:outline-purple-600 col-start-1 row-start-1 appearance-none rounded-sm border border-gray-300 bg-white focus-visible:outline-2 focus-visible:outline-offset-2 disabled:border-gray-300 disabled:bg-gray-100 disabled:checked:bg-gray-100 forced-colors:appearance-auto"
                      />
                      <svg
                        viewBox="0 0 14 14"
                        fill="none"
                        className="pointer-events-none col-start-1 row-start-1 size-7/8 self-center justify-self-center stroke-white"
                      >
                        <path
                          d="M3 8L6 11L11 3.5"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="group-not-has-checked:opacity-0"
                        />
                      </svg>
                    </span>
                  </span>
                  <span className="space-y-0.5 leading-snug">
                    <span className="block font-medium text-neutral-900">
                      Get occasional updates from Sked Social about social media best practices
                    </span>
                    <span className="block text-xs text-neutral-500">
                      Optional. Uncheck to skip, or unsubscribe at any time.
                    </span>
                  </span>
                </label>
              </div>
            )}

            {inAppBrowser ? null : (
              <>
                <p className="mt-8 text-xs leading-[1.5] text-neutral-500 text-pretty">
                  Scopes requested:{" "}
                  <code className="rounded bg-purple-50 px-1 py-0.5 text-[11px] font-mono text-purple-700">
                    analytics.readonly
                  </code>{" "}
                  plus your email and profile for sign-in. We also ask for offline access, which
                  usually lets your report reload without signing in again. Disconnect in{" "}
                  {authenticated ? (
                    <Link
                      to="/app/settings"
                      className="text-purple-500 underline underline-offset-4 hover:text-purple-700"
                    >
                      Settings
                    </Link>
                  ) : (
                    "Settings"
                  )}{" "}
                  removes our access and deletes your data.
                </p>

                <p className="mt-3 text-[11px] leading-[1.5] text-neutral-500">
                  By connecting GA4, you agree to the Sked Social{" "}
                  <a
                    href="https://skedsocial.com/terms-of-service"
                    target="_blank"
                    rel="noreferrer"
                    className="text-purple-500 underline underline-offset-2 hover:text-purple-700"
                  >
                    Terms of Service
                  </a>{" "}
                  and{" "}
                  <a
                    href="https://skedsocial.com/privacy-policy"
                    target="_blank"
                    rel="noreferrer"
                    className="text-purple-500 underline underline-offset-2 hover:text-purple-700"
                  >
                    Privacy Policy
                  </a>
                  .
                </p>
              </>
            )}
          </div>

          {inAppBrowser ? null : (
            <>
              <div className="mt-6">
                <p className="mb-3 text-center text-sm text-neutral-700">
                  Not ready to connect? See what you get first.
                </p>
                <SampleReportTeaser to={`/sample${location.search}`} />
              </div>

              <p className="mt-6 text-center text-sm">
                <Link to="/" className="ts-link">
                  Back to Traffic Snapshot <span className="ts-link-arrow">→</span>
                </Link>
              </p>
            </>
          )}
        </div>
      </main>

      <AppFooter />
    </div>
  );
}
